/**
 * 本地路由配置处理工具
 * 将routes.ts配置转换为菜单数据格式
 */
// 菜单项类型定义
interface MenuItem {
  name: string;
  path: string;
  icon?: string;
  redirect?: string;
  routes?: MenuItem[];
}

// 从routes.ts导入路由配置
import routesConfig from '../../config/routes';

/**
 * 将路由配置转换为菜单项
 */
export const convertRoutesToMenuItems = (routes: any[]): MenuItem[] => {
  return routes
    .filter((route) => route.name && route.path) // 只处理有名称和路径的路由
    .map((route) => {
      const menuItem: MenuItem = {
        name: route.name,
        path: route.path,
        icon: route.icon,
        redirect: route.redirect,
      };

      // 处理子路由
      if (route.routes && route.routes.length > 0) {
        const children = convertRoutesToMenuItems(route.routes);
        if (children.length > 0) {
          (menuItem as any).routes = children;
        }
      }

      return menuItem;
    });
};

/**
 * 获取本地路由配置的菜单数据
 */
export const getLocalRouteMenus = (): MenuItem[] => {
  // 过滤掉认证相关路由和其他不需要显示的路由
  const filteredRoutes = routesConfig.filter((route) => {
    // 排除登录页面
    if (route.path === '/login') return false;
    // 排除404页面
    if (route.path === '*') return false;
    // 排除根路径重定向
    if (route.path === '/') return false;

    return true;
  });

  return convertRoutesToMenuItems(filteredRoutes);
};

/**
 * 根据路径查找对应的路由配置
 */
export const findRouteByPath = (
  path: string,
  routes: any[] = routesConfig,
): any => {
  for (const route of routes) {
    if (route.path === path) {
      return route;
    }

    // 递归查找子路由
    if (route.routes && route.routes.length > 0) {
      const found = findRouteByPath(path, route.routes);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

/**
 * 获取所有路由路径（包括嵌套路由）
 */
export const getAllRoutePaths = (routes: any[] = routesConfig): string[] => {
  const paths: string[] = [];

  const traverse = (routeItems: any[]) => {
    routeItems.forEach((route) => {
      if (route.path) {
        paths.push(route.path);
      }

      if (route.routes && route.routes.length > 0) {
        traverse(route.routes);
      }
    });
  };

  traverse(routes);
  return paths;
};

/**
 * 检查路径是否存在本地路由配置中
 */
export const isPathInLocalRoutes = (path: string): boolean => {
  const allPaths = getAllRoutePaths();
  return (
    allPaths.includes(path) ||
    allPaths.some(
      (localPath) => path.startsWith(localPath) && path !== localPath,
    )
  );
};

/**
 * 获取本地路由的权限码（基于路径）
 */
export const getLocalRoutePermissions = (): string[] => {
  const allPaths = getAllRoutePaths();
  // 为每个路径生成权限码
  return allPaths.map((path) => `route:${path}`);
};

export default {
  convertRoutesToMenuItems,
  getLocalRouteMenus,
  findRouteByPath,
  getAllRoutePaths,
  isPathInLocalRoutes,
  getLocalRoutePermissions,
};
